import {
  alpha,
  Box,
  Button,
  ButtonBase,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DEFAULT_APPEARANCE, FILE_COLOR_PRESETS, FONT_FAMILIES } from '../utils/constants';
import { getReadableTextColor } from '../utils/colors';

export default function AppearanceControls({ appearance, onChange, onReset, resetLabel = 'Reset defaults' }) {
  const theme = useTheme();
  const value = { ...DEFAULT_APPEARANCE, ...appearance };

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="overline" color="text.secondary">
            Font Size
          </Typography>
          <Typography variant="body2" fontWeight={800} color="text.primary">
            {value.fontSize}px
          </Typography>
        </Stack>
        <Slider
          value={value.fontSize}
          min={12}
          max={36}
          step={1}
          valueLabelDisplay="auto"
          onChange={(_, nextValue) => onChange({ fontSize: nextValue })}
        />
      </Box>

      <FormControl fullWidth size="small">
        <InputLabel id="font-family-label">Font Family</InputLabel>
        <Select
          labelId="font-family-label"
          label="Font Family"
          value={value.fontFamily}
          onChange={(event) => onChange({ fontFamily: event.target.value })}
        >
          {FONT_FAMILIES.map((font) => (
            <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.value }}>
              {font.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="overline" color="text.secondary">
            Line Height
          </Typography>
          <Typography variant="body2" fontWeight={800} color="text.primary">
            {Number(value.lineHeight).toFixed(1)}
          </Typography>
        </Stack>
        <Slider
          value={value.lineHeight}
          min={1.2}
          max={2.4}
          step={0.1}
          valueLabelDisplay="auto"
          onChange={(_, nextValue) => onChange({ lineHeight: nextValue })}
        />
      </Box>

      <Box>
        <Typography variant="overline" color="text.secondary">
          Page Color
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))',
            gap: 1,
            mt: 0.75,
          }}
        >
          {FILE_COLOR_PRESETS.map((preset) => {
            const selected =
              value.backgroundColor.toLowerCase() === preset.value.toLowerCase();
            const textColor = getReadableTextColor(preset.value);

            return (
              <ButtonBase
                key={preset.value}
                onClick={() =>
                  onChange({
                    backgroundColor: preset.value,
                    textColor,
                  })
                }
                sx={{
                  position: 'relative',
                  display: 'block',
                  width: '100%',
                  minHeight: 82,
                  border: 1,
                  borderColor: selected ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: preset.value,
                  color: textColor,
                  textAlign: 'left',
                  overflow: 'hidden',
                  boxShadow: selected
                    ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.18)}`
                    : 'none',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
              >
                <Box sx={{ p: 1.25 }}>
                  <Typography variant="subtitle2" fontWeight={900} noWrap>
                    Aa
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.82 }} noWrap>
                    {preset.label}
                  </Typography>
                </Box>
                {selected ? (
                  <CheckCircleIcon
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      fontSize: 18,
                      color: textColor,
                    }}
                  />
                ) : null}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>
      {onReset ? (
        <Button variant="outlined" onClick={onReset}>
          {resetLabel}
        </Button>
      ) : null}
    </Stack>
  );
}
